import { VIEWER_CONTEXTS, type ViewerContext } from '@aysar/contracts';
import { useCallback, useMemo } from 'react';

import {
  defaultChoiceFor,
  isSectionDefaultMeaningful,
  isSectionPatchStricterThanPublic,
  modeChoiceFor,
  sectionOutcomeKeyFor,
  withSectionDefault,
  withSectionMode,
  type SectionDefaultChoice,
  type SectionModeChoice,
  type SectionVisibilityPatch,
} from '../../utils/question-visibility/question-visibility.util';

import { type SectionVisibilityDefaultsRow } from './SectionVisibilityDefaultsMatrix.types';

export interface UseSectionVisibilityDefaultsMatrixLogicInput {
  value: SectionVisibilityPatch;
  onChange: (patch: SectionVisibilityPatch) => void;
}

export interface UseSectionVisibilityDefaultsMatrixLogic {
  rows: readonly SectionVisibilityDefaultsRow[];
  setMode: (context: ViewerContext, choice: SectionModeChoice) => void;
  setStart: (context: ViewerContext, choice: SectionDefaultChoice) => void;
}

/**
 * The section-wide patch as data: one row per {@link ViewerContext}, each
 * carrying its two choices, whether the starting position is reachable, the
 * plain-language outcome of what has been named so far, and whether IT is the
 * row a stricter-than-public conflict belongs to.
 *
 * `useVisibilityMatrixLogic`'s counterpart for a value where **absence is a
 * state**, not a missing one: every derivation goes through the section helpers
 * so "unchanged" can never leak into the payload as a value.
 */
export function useSectionVisibilityDefaultsMatrixLogic({
  value,
  onChange,
}: UseSectionVisibilityDefaultsMatrixLogicInput): UseSectionVisibilityDefaultsMatrixLogic {
  const isConflicted = isSectionPatchStricterThanPublic(value);

  const rows = useMemo<readonly SectionVisibilityDefaultsRow[]>(
    () =>
      VIEWER_CONTEXTS.map((context) => ({
        context,
        mode: modeChoiceFor(value, context),
        start: defaultChoiceFor(value, context),
        isStartMeaningful: isSectionDefaultMeaningful(value, context),
        outcomeKey: sectionOutcomeKeyFor(value, context),
        showsStricterWarning: isConflicted && context === 'matched',
      })),
    [isConflicted, value],
  );

  const setMode = useCallback(
    (context: ViewerContext, choice: SectionModeChoice) => {
      onChange(withSectionMode(value, context, choice));
    },
    [onChange, value],
  );

  const setStart = useCallback(
    (context: ViewerContext, choice: SectionDefaultChoice) => {
      onChange(withSectionDefault(value, context, choice));
    },
    [onChange, value],
  );

  // No `isEmpty` here on purpose: "nothing named yet" is said once, by the
  // contract's own refusal on the field (`invalidMessage`), and a second copy in
  // the control would print the same sentence twice the moment a submit is
  // refused.
  return { rows, setMode, setStart };
}
